import { z } from 'zod';
import { safeFloat } from '@/modules/safe-float';

// Unit brands for money-like and ratio-like wire fields — the type IS the
// unit statement. ⚠️ Units are inconsistent across backends and endpoints —
// accepted, never "normalize": every such field carries its unit in its
// type. The confusions these brands exist to prevent have shipped:
// formatEther on an already-decimal value ("0 SAN" render), BigInt() on a
// fractional string (throw), and percent-vs-fraction ×100 ambiguity.
// Brands originate ONLY at the blessing schemas below; arithmetic strips
// them.
//
// WeiString: integer wei — ÷1e18 for display (formatEther after BigInt()).
export type WeiString = string & { readonly __brand: 'WeiString' };
// DecimalSanString: already-÷1e18 decimal SAN — Number() to read (BigInt()
// throws on the fractional values).
export type DecimalSanString = string & {
  readonly __brand: 'DecimalSanString';
};
// Pct: already percent (0.36 = 0.36%) — render with a "%" suffix, never
// rescale.
export type Pct = number & { readonly __brand: 'Pct' };
// Ratio01: a 0..1 fraction — ×100 at display.
export type Ratio01 = number & { readonly __brand: 'Ratio01' };
// DecimalRatioString: a 0..1 fraction as a STRING ("0.0357" = 3.57%) —
// Number() then ×100 at display.
export type DecimalRatioString = string & {
  readonly __brand: 'DecimalRatioString';
};

// The ONE blessing site per brand — every branded field parses through the
// matching schema here. The numeric brands coerce through
// @/modules/safe-float (backends ship numbers as strings); the string
// brands pass through untouched.
export const weiString = z.string().transform((s) => s as WeiString);
export const decimalSanString = z
  .string()
  .transform((s) => s as DecimalSanString);
export const pct = safeFloat.transform((n) => n as Pct);
export const ratio01 = safeFloat.transform((n) => n as Ratio01);
export const decimalRatioString = z
  .string()
  .transform((s) => s as DecimalRatioString);
